/**
 * LayersEventManager is a singleton that helps with keyboard handling in a layered or stacked UI.
 *
 * UI components register window scope key event listeners with the manager and their callback will
 * be called in a stacked context in cronological order, last one first. Each layer can handle
 * their callbacks and have the ability to stop event propagation down the layer stack.
 *
 * Example:
 * When you have multiple different pieces of UI listening for keyboard events, like a popped up
 * modal with an active dropdown component, with both of them closing on ESC, but when both are open
 * you only want the dropdown to close, not the modal.
 *
 * Without a stacked approach, where all UI components register their own key handlers on window,
 * all of them will always be called, regardless of stopped propagation, event registering order
 * or anything else.
 *
 * With the LayerEventsManager you can control how key events are handled and how they propagate
 * down your UI layers an avoid triggering all handlers at once without control
 */

define('lib/LayersEventManager',['require','lib/Podio.Utils'],function(require) {
  'use strict';

  var utils = require('lib/Podio.Utils');

  var layersStack = [];
  var generalEventListeners = {};

  var getLayerData = function(layer){
    for (var i=0; i<layersStack.length; i++){
      if(layersStack[i].layer === layer){
        return layersStack[i];
      }
    }
  };

  return {
    _throwWarning: function(text){
      try {
        throw new Error(text);
      } catch (err) {
        console.warn(err.stack); // jshint ignore:line
      }
    },

    _isInputEventTarget: function(target){
      if(target && target.tagName){
        return target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA';
      }
      return false;
    },

    register: function(layer, eventType, listener, allowPropagation, inputPropagation){
      var layerData = getLayerData(layer);
      if(!layerData){ // if layer does not exist, put it on the list
        layerData = {layer: layer, handlers: {}, allowPropagation: {}, inputPropagation: {}};
        layersStack.push(layerData);
      }

      layerData.allowPropagation[eventType] = !!allowPropagation;
      layerData.inputPropagation[eventType] = !!inputPropagation;
      layerData.handlers[eventType] = listener; // add/update listener for a given eventType

      // Add general listener for a specific event.type if not exist
      if(!generalEventListeners[eventType]){
        generalEventListeners[eventType] = function(event){
          this.dispatchEvent(event);
        }.bind(this);
        window.addEventListener(eventType, generalEventListeners[eventType]);
      }
    },

    unregister: function(layer){
      layersStack = layersStack.filter(function(layerData){
        return layerData.layer !== layer;
      });
    },

    dispatchEvent: function(event){
      var stopPropagation = false;
      event.stopPropagation = function(){
        stopPropagation = true;
      };

      var stack = layersStack.slice().reverse();

      // go through registered layers from top to bottom
      // return true to stop propagation
      stack.some(function(layerData){
        var handler = layerData.handlers[event.type];

        if(!layerData.inputPropagation[event.type] && this._isInputEventTarget(event.target)){
          return true;
        }

        if (handler) {
          handler(event);

          // Handler called event.stopPropagation
          if (stopPropagation) {
            return true;
          }
        }

        // Propagate event if allowPropagation was set to true when registering layer
        return !layerData.allowPropagation[event.type];
      }.bind(this));
    },

    clear: function(){
      layersStack = [];
      Object.keys(generalEventListeners).forEach(function(eventType){
        window.removeEventListener(eventType, generalEventListeners[eventType]);
      });
      generalEventListeners = {};
    }
  };
});

